import React from 'react';

const TermsOfUse = () => {
  return (
    <div className="font-body container mx-auto px-4 py-8">
      <header className="mb-8 border-b border-gray-200 dark:border-gray-700 pb-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Chameleon Eye, LLC
        </p>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mt-2 mb-2">Terms of Use</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">Last Updated: March 19, 2026</p>
      </header>

      <main className="max-w-3xl text-gray-800 dark:text-gray-200">
        <p className="leading-relaxed mb-6">
          Welcome to Chameleon Eye, LLC (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). These Terms of
          Use (&quot;Terms&quot;) govern your use of our website, applications, and related services
          (collectively, the &quot;Service&quot;). By accessing or using the Service, you agree to comply
          with these Terms. If you do not agree, you may not use the Service.
        </p>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 1
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">
            Acceptance of Terms
          </h2>
          <p>
            By using the Service, you agree to these Terms and our Privacy Policy. We reserve the right
            to modify these Terms at any time. Any changes will be posted on the Service, and your
            continued use of the Service after such changes constitutes your acceptance of the revised
            Terms.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 2
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">
            Access and Use of the Service
          </h2>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">a. Eligibility</h3>
          <p className="mb-4">
            You must be at least 18 years old to use the Service. If you are under the age of majority in
            your jurisdiction, you must have the consent of a legal guardian to use the Service.
          </p>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">b. Personal Use</h3>
          <p className="mb-4">
            The Service is for your personal and non-commercial use only. You may not use the Service for
            the benefit of any third party without our prior written consent.
          </p>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">
            c. Account Registration
          </h3>
          <p>
            To access certain features of the Service, you may need to create an account. You agree to
            provide accurate and complete information and to update it as necessary. You are responsible
            for maintaining the confidentiality of your account credentials.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 3
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">
            Fees and Payment
          </h2>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">
            a. Subscription Services
          </h3>
          <p className="mb-4">
            The Service may include free and paid features. If you choose to subscribe to paid features,
            you agree to pay all applicable fees as specified at the time of purchase.
          </p>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">
            b. Automatic Renewal
          </h3>
          <p className="mb-4">
            Subscriptions will automatically renew unless canceled at least one (1) business day before the
            end of the current billing period. By subscribing, you authorize us to charge your payment
            method for recurring payments.
          </p>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">c. Refunds</h3>
          <p>
            Payments are non-refundable, except as required by applicable law or at our discretion.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 4
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">User Content</h2>
          <p>
            You retain ownership of any content you submit to the Service (&quot;User Content&quot;). By
            submitting User Content, you grant us a worldwide, royalty-free license to use, reproduce,
            modify, display, and distribute your content in connection with the Service. You represent and
            warrant that your User Content does not infringe any third-party rights.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 5
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">
            Prohibited Activities
          </h2>
          <p className="mb-3">You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Use the Service for unlawful purposes.</li>
            <li>Submit false, misleading, or infringing content.</li>
            <li>Attempt to interfere with or compromise the Service&apos;s security or integrity.</li>
            <li>Use automated tools to scrape or access the Service without our permission.</li>
            <li>Impersonate another person or entity.</li>
          </ul>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 6
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">
            Intellectual Property
          </h2>
          <p>
            All content provided through the Service, including text, graphics, and software, is owned by us
            or our licensors and is protected by intellectual property laws. You may not copy, modify,
            distribute, or create derivative works from this content without our prior written consent.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 7
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">Termination</h2>
          <p>
            We may terminate or suspend your access to the Service at our discretion, without notice, for any
            reason, including violation of these Terms. Upon termination, your rights to use the Service will
            cease immediately.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 8
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">
            Disclaimers and Limitation of Liability
          </h2>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">a. Disclaimers</h3>
          <p className="mb-4">
            The Service is provided &quot;as is&quot; without warranties of any kind, express or implied. We
            do not guarantee that the Service will be uninterrupted or error-free.
          </p>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4 mb-2">
            b. Limitation of Liability
          </h3>
          <p>
            To the fullest extent permitted by law, we are not liable for any indirect, incidental, or
            consequential damages arising from your use of the Service.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 9
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">
            Indemnification
          </h2>
          <p>
            You agree to indemnify and hold us harmless from any claims, losses, or damages arising from
            your use of the Service, your User Content, or your violation of these Terms.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 10
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">
            Third-Party Links
          </h2>
          <p>
            The Service may include links to third-party websites. We are not responsible for the content
            or practices of these websites.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 11
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">
            Governing Law and Dispute Resolution
          </h2>
          <p>
            These Terms are governed by the laws of Massachusetts, without regard to conflict of laws
            principles. Any disputes will be resolved through binding arbitration in Massachusetts.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 12
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">
            Changes to the Service
          </h2>
          <p>
            We may modify or discontinue the Service at any time without notice. We are not liable for any
            changes or interruptions to the Service.
          </p>
        </section>

        <section className="mb-10">
          <span className="block text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
            Section 13
          </span>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 mb-3">Contact Us</h2>
          <p className="mb-3">For questions about these Terms, please contact us at:</p>
          <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-4 my-2">
            <p>
              Email:{' '}
              <a
                className="text-blue-600 dark:text-blue-400 underline"
                href="mailto:support@makerstate.com"
              >
                support@makerstate.com
              </a>
              <br />
              <span className="text-sm opacity-70">Owned and operated by Chameleon Eye, LLC</span>
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default TermsOfUse;
